import { useState, useEffect } from 'react';
import './ProgressIndicator.css';

export default function ProgressIndicator({ stage, organ, segmentationStatus }) {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  const currentOrgan = segmentationStatus?.current_organ || null;
  const currentIndex = segmentationStatus?.current_index || 0;
  const totalOrgans = segmentationStatus?.total_organs || 0;
  const completedOrgans = segmentationStatus?.completed_organs || [];
  const failedOrgans = segmentationStatus?.failed_organs || [];

  const stages = [
    { id: 'upload', label: 'File Ready', icon: '📁', color: '#10b981' },
    { id: 'preprocessing', label: 'Preprocessing', icon: '⚙️', color: '#f59e0b' },
    { id: 'segmenting', label: 'AI Segmentation', icon: '🤖', color: '#8b5cf6' },
    { id: 'mesh', label: 'Generating 3D Mesh', icon: '🎨', color: '#3b82f6' },
    { id: 'complete', label: 'Complete!', icon: '✨', color: '#10b981' }
  ];

  const currentStageIndex = stages.findIndex(s => s.id === stage);

  return (
    <div className="progress-indicator mx-auto" style={{ maxWidth: '900px' }}>
      <div className="progress-header">
        <h3>Processing {organ}{dots}</h3>
        
        {(currentOrgan || totalOrgans > 1) && (
          <div className="organ-progress-info">
            {currentOrgan ? (
              <p className="current-organ-label">
                🔬 Currently segmenting: <strong>{currentOrgan}</strong> 
                {totalOrgans > 1 && <span> ({currentIndex}/{totalOrgans})</span>}
              </p>
            ) : (
              <p className="current-organ-label">
                ⏳ Starting segmentation of {totalOrgans} organs...
              </p>
            )}
            {completedOrgans.length > 0 && (
              <p className="completed-organs">
                ✓ Completed: {completedOrgans.join(', ')}
              </p>
            )}
            {failedOrgans.length > 0 && (
              <p className="failed-organs">
                ✗ Failed: {failedOrgans.join(', ')}
              </p>
            )}
          </div>
        )}
        <p className="progress-subtitle">This usually takes 1-2 minutes</p>
      </div>

      <div className="progress-pipeline">
        {stages.map((s, index) => {
          const isCompleted = index < currentStageIndex;
          const isCurrent = index === currentStageIndex;
          const isPending = index > currentStageIndex;

          return (
            <div key={s.id} className="pipeline-step">
              {/* Only show connector line between steps (not after the last step) */}
              {index > 0 && (
                <div className="step-connector">
                  <div className={`connector-line ${isCompleted ? 'completed' : ''}`} />
                </div>
              )}
              
              <div className={`step-circle ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''} ${isPending ? 'pending' : ''}`}>
                <span className="step-icon" style={{ fontSize: isCurrent ? '2rem' : '1.5rem' }}>
                  {s.icon}
                </span>
                {isCurrent && (
                  <div className="ripple-effect">
                    <div className="ripple"></div>
                    <div className="ripple"></div>
                    <div className="ripple"></div>
                  </div>
                )}
              </div>
              
              <div className="step-label">
                <span className={isCurrent ? 'current-label' : ''}>{s.label}</span>
                {isCurrent && <div className="pulse-dot"></div>}
              </div>
            </div>
          );
        })}
      </div>

      {stage === 'segmenting' && (
        <div className="ai-visualization">
          <div className="neural-network">
            <div className="neuron-layer">
              <div className="neuron"></div>
              <div className="neuron"></div>
              <div className="neuron"></div>
            </div>
            <div className="neuron-layer">
              <div className="neuron"></div>
              <div className="neuron"></div>
              <div className="neuron"></div>
              <div className="neuron"></div>
            </div>
            <div className="neuron-layer">
              <div className="neuron"></div>
              <div className="neuron"></div>
              <div className="neuron"></div>
            </div>
          </div>
          <p className="ai-message">TotalSegmentator AI is analyzing your scan...</p>
        </div>
      )}

      {stage === 'mesh' && (
        <div className="mesh-visualization">
          <div className="rotating-cube">
            <div className="cube-face front"></div>
            <div className="cube-face back"></div>
            <div className="cube-face right"></div>
            <div className="cube-face left"></div>
            <div className="cube-face top"></div>
            <div className="cube-face bottom"></div>
          </div>
          <p className="mesh-message">Building your 3D visualization...</p>
        </div>
      )}
    </div>
  );
}

